import { Suspense, useEffect, useRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { XR, useXR } from "@react-three/xr";
import { Text } from "@react-three/drei";
import { EffectComposer, Bloom } from "@react-three/postprocessing";
import { MacroScene } from "./MacroScene";
import { StreetSandbox } from "./StreetSandbox";
import { xrStore } from "./xrStore";
import { getDracoLoader } from "./loaders";
import { useSim } from "../lib/store";

function SimTicker() {
  useFrame(() => {
    useSim.getState().tickSim();
  });
  return null;
}

// Camera clip planes + fog are scale-specific: the macro globe is ~2 units, the
// 1:1 city is ~150 units. Switch them per view so the street isn't clipped/fogged to black.
function ViewConfig() {
  const viewMode = useSim((s) => s.viewMode);
  const { camera, scene } = useThree();
  useEffect(() => {
    if (viewMode === "street") {
      camera.near = 0.5;
      camera.far = 1200;
      scene.fog = new THREE.Fog("#0a0f1c", 140, 620);
    } else {
      camera.near = 0.1;
      camera.far = 100;
      scene.fog = new THREE.Fog("#030712", 14, 34);
    }
    camera.updateProjectionMatrix();
  }, [viewMode, camera, scene]);
  return null;
}

function PostFX() {
  const session = useXR((s) => s.session);
  const viewMode = useSim((s) => s.viewMode);
  if (session) return null; // postprocessing composer is bypassed in immersive XR
  return (
    <EffectComposer disableNormalPass>
      <Bloom
        intensity={viewMode === "street" ? 0.28 : 0.42}
        luminanceThreshold={0.62}
        luminanceSmoothing={0.9}
        mipmapBlur
        radius={0.55}
      />
    </EffectComposer>
  );
}

function SceneRouter() {
  const viewMode = useSim((s) => s.viewMode);
  return viewMode === "street" ? <StreetSandbox /> : <MacroScene />;
}

// VR spatial onboarding: a head-locked 3D slate that follows the user's gaze in the headset.
function VRTutorialSlate() {
  const session = useXR((s) => s.session);
  const active = useSim((s) => s.onboardActive);
  const title = useSim((s) => s.onboardTitle);
  const body = useSim((s) => s.onboardBody);
  const groupRef = useRef();
  const { camera } = useThree();
  const tmp = useRef(new THREE.Vector3()).current;

  useFrame(() => {
    if (!groupRef.current) return;
    tmp.set(0, -0.12, -1.2).applyQuaternion(camera.quaternion).add(camera.position);
    groupRef.current.position.lerp(tmp, 0.25);
    groupRef.current.quaternion.slerp(camera.quaternion, 0.35);
  });

  if (!session || !active) return null;
  return (
    <group ref={groupRef}>
      <mesh>
        <planeGeometry args={[0.95, 0.42]} />
        <meshBasicMaterial color="#0a0a12" transparent opacity={0.92} />
      </mesh>
      <Text position={[0, 0.11, 0.01]} fontSize={0.05} color="#c9a94a" anchorX="center" anchorY="middle" maxWidth={0.85}>
        {title}
      </Text>
      <Text position={[0, -0.05, 0.01]} fontSize={0.032} color="#e8e8ee" anchorX="center" anchorY="middle" textAlign="center" maxWidth={0.82}>
        {body}
      </Text>
    </group>
  );
}

export function Experience() {
  return (
    <Canvas
      shadows
      camera={{ fov: 45, position: [0, 1.2, 8], near: 0.1, far: 100 }}
      dpr={[1, 1.75]}
      gl={{ antialias: true, toneMapping: THREE.ACESFilmicToneMapping, powerPreference: "high-performance" }}
      onCreated={({ gl }) => {
        gl.setClearColor("#030712", 1);
        getDracoLoader(); // warm DRACO decode pipeline for compressed geometry
      }}
    >
      <color attach="background" args={["#030712"]} />
      <XR store={xrStore}>
        <ViewConfig />
        <Suspense fallback={null}>
          <SceneRouter />
        </Suspense>
        <SimTicker />
        <VRTutorialSlate />
        <PostFX />
      </XR>
    </Canvas>
  );
}
